import React, {ReactElement, CSSProperties, ChangeEvent, forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'
import _ from 'lodash'

import {Colors, Info, P, ToolTip, Popover, ListItem, Icon, Badge, Spinner, Row} from '../index.js'
import {normalize} from '@utils/normalize.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import FieldBox, {FieldCaret} from './FieldBox.js'
import {fieldControlClass} from './fieldStyles.js'
import {field, formColors, getFieldCursor, getFieldTextColor, getTrailingPadding} from './styles.js'
import {boldMatch, useFieldId} from './utils.js'
import type {PopoverRef} from '../overlays/Popover.js'

function sortSelectedFirst(options: SelectOption[], values?: string[]) {
    if (!values) {
        return options
    }

    return [...options].sort((a, b) => {
        const aSelected = a && values.includes(a.value?.toString()) ? 0 : 1
        const bSelected = b && values.includes(b.value?.toString()) ? 0 : 1
        return aSelected - bSelected
    })
}

function sortGroupedSubvalues(displayOptions: SelectOption[]) {
    return displayOptions.sort((a, b) => {
        const aValueTitle = displayOptions.find((option) => option.value === a.value)?.title
        const bValueTitle = displayOptions.find((option) => option.value === b.value)?.title
        let aStr = `${aValueTitle}${a.value}${a.disabled && a.subvalue ? 'zzz' : ''}${a.subvalue ? ` - ${a.title}` : ''}`
        let bStr = `${bValueTitle}${b.value}${b.disabled && b.subvalue ? 'zzz' : ''}${b.subvalue ? ` - ${b.title}` : ''}`

        if (a.disabled && !a.subvalue) {
            aStr = `zzz${aStr}`
        }

        if (b.disabled && !b.subvalue) {
            bStr = `zzz${bStr}`
        }

        return aStr.localeCompare(bStr)
    })
}

// The visible options: not hidden, matching the search query, sorted like the popover shows them
function filterDisplayOptions(options: SelectOption[], query: string, groupSubvalues?: boolean, values?: string[]) {
    const normalizedQuery = normalize(query.toLowerCase())

    let displayOptions = (options || []).filter((option) => {
        return !!option && !option.hidden && typeof option.title === 'string' && normalize(option.title.toLowerCase()).indexOf(normalizedQuery) > -1
    })

    if (groupSubvalues) {
        displayOptions = sortGroupedSubvalues(displayOptions)
    }

    return sortSelectedFirst(displayOptions, values)
}

// Options can contain functions and other non-serializable values, so we create a "safe" version of the options that only contains the serializable values to compare with the previous props
function safeOptions(options: SelectOption[]) {
    return options?.filter((option) => option).map((option) => {
        return {
            value: option.value,
            title: option.title,
            hidden: option.hidden,
            disabled: option.disabled,
            subvalue: option.subvalue,
            icon: option.icon,
            iconColor: option.iconColor,
            showIconInValue: option.showIconInValue,
            badge: option.badge
        }
    })
}

function optionsFromChildren(children: ReactElement[]) {
    return children.map((child) => {
        if (child && child.props) {
            return {value: child.props.value, title: child.props.children, disabled: child.props.disabled}
        }
    })
}

function initialOptions(props: SelectProps) {
    let options = props.options

    if (props.fetch) {
        options = []
    }

    if (!options && props.children) {
        options = optionsFromChildren(props.children)
    }

    if (props.allowEmpty || props.placeholder) {
        options = [{value: '', title: props.placeholder || ''}, ...(options || [])]
    }

    if (props.allowCustomValues) {
        // Copy instead of push: options can still be the caller's options prop here
        (props.values || []).map((value) => {
            if (!(options || []).find((option) => option.value === value)) {
                options = [...(options || []), {value, title: value}]
            }
        })
    }

    options = options || []

    let displayOptions = options.filter((option) => {
        return !!option && !option.hidden
    })

    if (props.groupSubvalues) {
        displayOptions = sortGroupedSubvalues(displayOptions)
    }

    displayOptions = sortSelectedFirst(displayOptions, props.values)

    return {options, displayOptions}
}

/**
 * The event passed to onChange, with the result written onto its target: target.value (and target.subvalue when picking an option) for a single select, target.values for a multi select (values prop).
 * Typed as an input ChangeEvent because callers share handlers with Input, but at runtime it can also be the key press or mouse down event of an option, so only rely on target.value/values/subvalue.
 */
export interface SelectChangeEvent extends ChangeEvent<HTMLInputElement> {
    target: ChangeEvent<HTMLInputElement>['target'] & {
        values?: string[]
        subvalue?: string
    }
}

export interface SelectOption {
    value: string | number
    title: string
    hidden?: boolean
    disabled?: boolean
    subvalue?: string
    icon?: string
    iconColor?: string
    showIconInValue?: boolean
    badge?: string
    badgeColor?: string
    badgeTextColor?: string
    info?: string
    infoKeepWhiteSpace?: boolean
    bold?: boolean
    img?: string
}

export interface SelectProps extends FieldProps, InvalidProps {
    options?: SelectOption[]
    /** Called with true as second argument when a free mode value is added via "Voeg ... toe" */
    onChange?: (event: SelectChangeEvent, isFreeMode?: boolean) => void
    values?: string[]
    /** Multi select only: show an option to (de)select all options at once */
    selectAll?: boolean
    groupSubvalues?: boolean
    placeholder?: string
    /** Alternative to options: <option value disabled>title</option> elements */
    children?: ReactElement<{value: string | number, children: string, disabled?: boolean}>[]
    allowEmpty?: boolean
    allowCustomValues?: boolean
    noSort?: boolean
    showValue?: boolean
    allowFreeMode?: boolean
    freeModeAddValue?: boolean
    id?: string
    inputStyle?: CSSProperties
    infoIcon?: string
    value?: string | number
    subvalue?: string
    openAfter?: number
    loading?: boolean
    isInvalidIcon?: string
    onFocus?: () => void
    onBlur?: () => void
    /** Multi select only: show a button that copies the values as 'a;b;' (pasting such a string selects them) */
    copy?: boolean
    maxOptions?: number
    /** Maximum width of the list, longer options are cut off with an ellipsis. By default as wide as the longest option */
    listMaxWidth?: number
    /** false keeps the list below the field, by default it opens above when there is no room below */
    showOnlyIcon?: boolean
    indentSubvalues?: boolean
    fetch?: () => Promise<SelectOption[]>
    fetchTrigger?: string
}

export interface SelectRef extends FieldRef {
    getRef: () => HTMLInputElement | null
    /** True while the options popover is open */
    focused: () => boolean
}

const Select = forwardRef<SelectRef, SelectProps>((props, ref) => {
    const {id, style = {}, inputStyle = {}, label, info, infoIcon, value = '', subvalue = '', values, readOnly, disabled, loading, isInvalid, isInvalidIcon, copy, allowFreeMode, freeModeAddValue = false, allowCustomValues, maxOptions = 400, listMaxWidth, showOnlyIcon, indentSubvalues = true, selectAll, noSort, groupSubvalues, onChange} = props

    const [initial] = useState(() => initialOptions(props))
    const [focused, setFocused] = useState(false)
    const [isInvalidState, setIsInvalidState] = useState(false)
    const [open, setOpen] = useState(false)
    const [optionsLoading, setOptionsLoading] = useState(false)
    const [options, setOptions] = useState<SelectOption[]>(initial.options)
    const [displayOptions, setDisplayOptions] = useState<SelectOption[]>(initial.displayOptions)
    const [inputValue, setInputValue] = useState('')
    const [selectedValue, setSelectedValue] = useState(0)
    const [showCopyTooltip, setShowCopyTooltip] = useState(false)
    const [visibleValuesCount, setVisibleValuesCount] = useState(props.values ? props.values.length : 0)

    const fieldId = useFieldId(id)

    const popoverRef = useRef<PopoverRef>(null)
    // The bordered box, the list opens at it
    const boxRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const tagsRowRef = useRef<HTMLDivElement>(null)
    const overflowTagRef = useRef<HTMLDivElement>(null)
    const valueRefs = useRef<(HTMLDivElement | null)[]>([])
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

    // Latest props/state for callbacks that outlive the render they were created in (timeouts, ResizeObserver, async fetch)
    const propsRef = useRef(props)
    propsRef.current = props
    const visibleValuesCountRef = useRef(visibleValuesCount)
    visibleValuesCountRef.current = visibleValuesCount
    const inputValueRef = useRef(inputValue)
    inputValueRef.current = inputValue

    const prevRef = useRef<{props: SelectProps, inputValue: string}>(null)
    // Incremented per fetch and on unmount, so only the latest fetch of a mounted Select sets state
    const fetchIdRef = useRef(0)

    const fetchOptions = async () => {
        if (propsRef.current.fetch) {
            const fetchId = ++fetchIdRef.current
            setOptionsLoading(true)
            let options: SelectOption[] = propsRef.current.value ? [{title: `${propsRef.current.value}`, value: propsRef.current.value}] : []

            try {
                options = await propsRef.current.fetch()
            } catch (err) {
                console.log(err)
            }

            if (fetchId !== fetchIdRef.current) {
                return
            }

            if (propsRef.current.allowEmpty || propsRef.current.placeholder) {
                options = [{value: '', title: propsRef.current.placeholder || ''}, ...options]
            }

            setOptions(options)
            setDisplayOptions(filterDisplayOptions(options, inputValueRef.current, propsRef.current.groupSubvalues, propsRef.current.values))
            setOptionsLoading(false)
        }
    }

    const measureValues = () => {
        const {values} = propsRef.current
        const tagsRow = tagsRowRef.current

        if (!values || !tagsRow) {
            return
        }

        if (!values.length) {
            if (visibleValuesCountRef.current !== 0) {
                setVisibleValuesCount(0)
            }
            return
        }

        const availableWidth = tagsRow.clientWidth
        const gap = 6

        const widths = values.map((value, index) => {
            const ref = valueRefs.current[index]
            return ref ? ref.getBoundingClientRect().width + gap : 0
        })

        const overflowTagWidth = (overflowTagRef.current ? overflowTagRef.current.getBoundingClientRect().width : 0) + gap

        let fitCount = values.length
        let total = 0

        for (let i = 0; i < widths.length; i++) {
            total += widths[i]

            const isLast = i === widths.length - 1
            const reserve = isLast ? 0 : overflowTagWidth

            if (total + reserve > availableWidth) {
                fitCount = i
                break
            }
        }

        if (fitCount !== visibleValuesCountRef.current) {
            setVisibleValuesCount(fitCount)
        }
    }

    // Layout effects to keep the synchronous timing of componentDidMount/componentDidUpdate (measuring before paint)
    useLayoutEffect(() => {
        fetchOptions()

        return () => {
            clearTimeout(timeoutRef.current)
            fetchIdRef.current++
        }
    }, [])

    // The tags row only exists while values is an array, so (re)observe it whenever that changes
    const hasValues = Array.isArray(values)

    useLayoutEffect(() => {
        if (!hasValues || typeof ResizeObserver === 'undefined' || !tagsRowRef.current) {
            return
        }

        const resizeObserver = new ResizeObserver(() => measureValues())
        resizeObserver.observe(tagsRowRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [hasValues])

    // Runs after every update, comparing with the previous props like componentDidUpdate did
    useLayoutEffect(() => {
        const prev = prevRef.current
        prevRef.current = {props, inputValue}

        if (!prev) {
            return
        }

        const prevProps = prev.props
        const {children = []} = props

        const newKeys = children.map((child) => {
            if (child) {
                return child.key
            }
        })
        const oldKeys = (prevProps.children || []).map((child) => {
            if (child) {
                return child.key
            }
        })

        // The same array has not changed: only a new one is compared, which is slow for thousands of options
        const optionsChanged = props.options !== prevProps.options && JSON.stringify(safeOptions(props.options)) !== JSON.stringify(safeOptions(prevProps.options))
        const valuesChanged = JSON.stringify(values) !== JSON.stringify(prevProps.values)

        if (valuesChanged || optionsChanged || JSON.stringify(newKeys) !== JSON.stringify(oldKeys) || prev.inputValue !== inputValue) {
            let newOptions = props.options

            if (!newOptions && props.children) {
                newOptions = optionsFromChildren(props.children)
            }

            if (props.allowEmpty || props.placeholder) {
                newOptions = [{value: '', title: props.placeholder || ''}, ...(newOptions || [])]
            }

            if (props.allowCustomValues) {
                (props.values || []).map((value) => {
                    if (!(newOptions || []).find((option) => option.value === value)) {
                        newOptions = [...(newOptions || []), {value, title: value}]
                    }
                })
            }

            if (props.fetch) {
                newOptions = options || []
            }

            const newDisplayOptions = filterDisplayOptions(newOptions, inputValue, groupSubvalues, values)

            setOptions(newOptions)
            setDisplayOptions(newDisplayOptions)
            setSelectedValue(0)
        }

        if (prevProps.fetchTrigger !== props.fetchTrigger) {
            fetchOptions()
        }
    })

    // Measure after the pills have rendered with the current options and values. Measuring in the effect above
    // was too early: it calls setOptions, so the pills still showed the old options (0 width when not found yet)
    const valuesKey = values ? JSON.stringify(values) : undefined

    useLayoutEffect(() => {
        measureValues()
    }, [options, valuesKey])

    // Every change goes through here: disabled and readOnly Selects never call onChange, and a change clears setInvalid's state
    const emitChange = (event, isFreeMode?: boolean) => {
        if (disabled || readOnly) {
            return
        }

        setIsInvalidState(false)

        if (isFreeMode) {
            onChange?.(event, true)
        } else {
            onChange?.(event)
        }
    }

    const onChangeInput = (event) => {
        if (disabled || readOnly) {
            return
        }

        const {openAfter = 0} = props
        const popover = popoverRef.current

        if (popover.closed() && event.target.value.length >= openAfter) {
            popover.show()
        }

        if (!popover.closed() && event.target.value.length < openAfter) {
            popover?.close()
        }

        if (event.target.value.indexOf(';') > -1 && values) {
            const inputValues = event.target.value.split(';')
            // Copy so the caller's values prop is not mutated
            const newValues = [...values]

            inputValues.map((inputValue) => {
                const option = _.find(options, (option) => {
                    // Compare as strings: option values can be numbers
                    return !!option && inputValue === option.value?.toString()
                })

                if (option && newValues.indexOf(option.value?.toString()) === -1) {
                    newValues.push(option.value?.toString())
                }
            })

            if (!noSort) {
                event.target.values = _.sortBy(newValues, (value) => _.findIndex(options, (option) => option?.value?.toString() === value))
            } else {
                event.target.values = newValues
            }

            setInputValue('')
            emitChange(event)
        } else {
            const inputValue = event.target.value

            const displayOptions = filterDisplayOptions(options, inputValue, groupSubvalues, values)

            if (allowFreeMode && !freeModeAddValue) {
                event.target.value = inputValue
                setInputValue(inputValue)
                setDisplayOptions(displayOptions)
                emitChange(event)
            } else {
                setInputValue(inputValue)
                setDisplayOptions(displayOptions)
            }
        }
    }

    const onKeyDown = (event) => {
        const {openAfter = 0} = props

        if (disabled || readOnly) {
            return
        }

        if (event.keyCode === 40 || event.which === 40) { // Down
            if (popoverRef.current.closed()) {
                if (inputValue.length >= openAfter) {
                    event.preventDefault()
                    popoverRef.current.show()
                }
            } else if (selectedValue < displayOptions.length - 1) {
                event.preventDefault()
                setSelectedValue(selectedValue + 1)
            }
        }

        if ((event.keyCode === 38 || event.which === 38) && selectedValue > 0) { // Up
            event.preventDefault()

            setSelectedValue(selectedValue - 1)
        }
    }

    const onKeyPress = (event) => {
        if (disabled || readOnly) {
            return
        }

        if (!popoverRef.current.closed() && (event.keyCode === 13 || event.which === 13)) { // Enter
            event.preventDefault()

            const option = displayOptions[selectedValue]

            if (allowCustomValues && !displayOptions.length) {
                onClickAddValue(event)
                return
            }

            // No option highlighted (e.g. the search matches nothing), so there is nothing to select
            if (!option) return

            if (values) {
                const index = values.findIndex((item) => item === option?.value.toString())

                if (index === -1) {
                    if (!noSort) {
                        event.target.values = _.sortBy([...values, option?.value.toString()], (value) => _.findIndex(options, (option) => option?.value.toString() === value))
                    } else {
                        event.target.values = [...values, option?.value.toString()]
                    }
                } else {
                    event.target.values = [...values.slice(0, index), ...values.slice(index + 1)]
                }
            } else {
                event.target.value = option?.value.toString() || ''
                event.target.subvalue = option?.subvalue?.toString() || ''
            }

            setInputValue('')
            setSelectedValue(0)
            emitChange(event)

            if (!values) {
                popoverRef.current?.close()
            }
        }
    }

    /**
     * Selects all (enabled, visible) options, or deselects them when they are all selected already.
     * With noSort the current selection keeps its order and the remaining options are appended.
     */
    const onClickSelectAll = (event) => {
        event.preventDefault()

        const selectableValues = options.filter((option) => option && !option.disabled && !option.hidden).map((option) => option.value.toString())
        const allSelected = selectableValues.every((value) => values.includes(value))

        if (allSelected) {
            event.target.values = values.filter((value) => !selectableValues.includes(value))
        } else {
            const newValues = [...values, ...selectableValues.filter((value) => !values.includes(value))]
            event.target.values = noSort ? newValues : _.sortBy(newValues, (value) => _.findIndex(options, (option) => option?.value.toString() === value))
        }

        setInputValue('')
        setSelectedValue(0)
        emitChange(event)

        setTimeout(() => focusActiveInput(), 10)
    }

    const onClickAddValue = (event) => {
        // Uses the raw value prop: the '' default above would make the typeof check below always pass
        const {value} = props
        event.preventDefault()

        const valuesCopy = values ? [...values] : undefined
        // Children based Selects have no options prop
        const newOptions = [...(props.options || options)]

        if (valuesCopy && inputValue) {
            newOptions.push({value: inputValue, title: inputValue})
            event.target.values = _.sortBy([...valuesCopy, inputValue], (val) => _.findIndex(newOptions, (option) => option?.value === val))

            setInputValue('')
            setSelectedValue(0)
            emitChange(event)
        } else if (typeof value !== 'undefined' && freeModeAddValue && inputValue) {
            newOptions.push({value: inputValue, title: inputValue})
            event.target.value = inputValue

            setInputValue('')
            setSelectedValue(0)
            emitChange(event, true)
            popoverRef.current?.close()
        }
    }

    const onClickOption = (option, event) => {
        event.preventDefault()

        if (values) {
            const index = values.findIndex((item) => item === option?.value.toString())

            if (index === -1) {
                if (!noSort) {
                    event.target.values = _.sortBy([...values, option?.value.toString()], (value) => _.findIndex(options, (option) => option?.value.toString() === value))
                } else {
                    event.target.values = [...values, option?.value.toString()]
                }
            } else {
                event.target.values = [...values.slice(0, index), ...values.slice(index + 1)]
            }
        } else {
            event.target.value = option?.value.toString() || ''
            event.target.subvalue = option?.subvalue?.toString() || ''
        }

        setInputValue('')
        setSelectedValue(0)
        emitChange(event)

        setTimeout(() => {
            focusActiveInput()

            if (!values) {
                popoverRef.current?.close()
            }
        }, 10)
    }

    const pillStyle = (): CSSProperties => {
        return {
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            height: 24,
            marginRight: 6,
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1,
            paddingLeft: 9,
            paddingRight: 9,
            borderRadius: 7,
            background: Colors.grey10,
            border: `1px solid ${Colors.grey30}`,
            color: disabled ? Colors.textMedium : Colors.textExtraDark
        }
    }

    const renderValuePill = (value, index, ref?: (el: HTMLDivElement | null) => void) => {
        const option = _.find(options, (option) => option && value == option.value)

        if (!option) {
            return null
        }

        return (
            <div
                key={value}
                ref={ref}
                style={pillStyle()}
            >
                {option.showIconInValue && option.icon && (
                    <Icon
                        style={{color: option.iconColor, marginRight: 6}}
                        icon={option.icon}
                        size='xs'
                    />

                )} {option.title}
            </div>
        )
    }

    const onClickCopyValues = (event) => {
        event.stopPropagation()

        const text = `${values.join(';')};`

        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(text).then(() => {
                setShowCopyTooltip(true)
                clearTimeout(timeoutRef.current)
                timeoutRef.current = setTimeout(() => {
                    setShowCopyTooltip(false)
                }, 1200)
            }).catch((err) => {
                console.log(err)
            })
        }
    }

    const focus = () => {
        inputRef.current?.focus()
    }

    const focusActiveInput = () => {
        if (propsRef.current.values && searchInputRef.current) {
            searchInputRef.current.focus()
        } else {
            inputRef.current?.focus()
        }
    }

    const handleFocus = () => {
        const {openAfter = 0, onFocus} = props

        if (disabled || readOnly) {
            return
        }

        if (inputValue.length >= openAfter) {
            popoverRef.current.show()
        }

        setFocused(true)

        if (values) {
            setTimeout(() => searchInputRef.current?.focus(), 0)
        }

        if (typeof onFocus === 'function') {
            onFocus()
        }
    }

    const handleBlur = (event) => {
        const {onBlur} = props
        const relatedTarget = event?.relatedTarget

        if (values && relatedTarget && (relatedTarget === inputRef.current || relatedTarget === searchInputRef.current)) {
            return
        }

        if (allowFreeMode) {
            setFocused(false)
        } else {
            setFocused(false)
            setInputValue('')
        }

        popoverRef.current?.close()

        if (typeof onBlur === 'function') {
            onBlur()
        }
    }

    useImperativeHandle(ref, () => ({
        focus,
        blur: () => {
            inputRef.current?.blur()
        },
        getRef: () => inputRef.current,
        focused: () => !popoverRef.current.closed(),
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            focus()
        }
    }), []) // The methods only use refs

    const allOptionsSelected = !!values && options.filter((option) => option && !option.disabled && !option.hidden).every((option) => values.includes(option.value.toString()))

    const option = _.find(options, (option) => option && value === option.value && subvalue === (option.subvalue || ''))

    const title = option ? option.title : (allowFreeMode ? value : '')

    const focusedState = optionsLoading || loading || focused || inputValue || title || (values && values.length > 0)

    const invalid = isInvalid || isInvalidState

    // Shown when no option is chosen: the empty option that allowEmpty/placeholder adds has the placeholder as title
    const showsPlaceholder = !!props.placeholder && !values && option?.value === ''

    // Distance from the right edge of the field to the left edge of the leftmost trailing control (caret, option info,
    // copy button). The invalid icon stays left of them, the shown value stops before them (or before the invalid icon).
    const trailingEdge = Math.max(
        readOnly ? 0 : 24,
        option?.info ? 50 : 0,
        copy ? (option?.info ? 69 : 39) : 0
    )
    const trailingWidth = trailingEdge ? trailingEdge - 4 : 0
    const valueRight = getTrailingPadding({invalid, trailingWidth, padding: Math.max(trailingEdge + 4, field.paddingX)})

    // Fills the box. Positioned, so the absolutely positioned controls below are placed against it, which has the same
    // edges as the outer container.
    const contentStyle: CSSProperties = {
        display: 'flex',
        flexWrap: 'nowrap',
        position: 'relative',
        width: '100%',
        height: '100%',
        paddingTop: field.paddingTop,
        paddingBottom: field.paddingBottom,
        // The typed text and the pills stop where the shown value does
        paddingRight: valueRight
    }

    const defaultInputStyle: CSSProperties = {
        fontSize: field.fontSize,
        background: 'transparent',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: field.paddingX,
        minWidth: values ? 2 : 100,
        width: values ? 2 : inputValue.length * 12,
        maxWidth: 'calc(100% - 15px)',
        outline: 'none',
        border: 'none',
        color: getFieldTextColor({disabled, readOnly}),
        cursor: getFieldCursor({disabled, readOnly}),
        ...inputStyle
    }

    const searchInputStyle: CSSProperties = {
        width: '100%',
        fontSize: 14,
        padding: '10px 12px',
        outline: 'none',
        border: 'none',
        borderBottom: `1px solid ${Colors.grey20}`,
        color: Colors.textDark,
        background: Colors.backgroundWhite
    }

    const overflowTagStyle: CSSProperties = {
        ...pillStyle(),
        marginRight: 0
    }

    return (
        <FieldBox
            label={label}
            info={info}
            infoIcon={infoIcon}
            floated={!!focusedState}
            focused={focused}
            invalid={invalid}
            invalidIconClass={isInvalidIcon}
            disabled={disabled}
            readOnly={readOnly}
            trailingWidth={trailingWidth}
            // Clicking the label focuses the input (which opens the list), or clicks it when focused already (reopens it)
            controlId={fieldId}
            style={style}
            // Stretch, so the content fills the box's full height
            boxStyle={{alignItems: 'stretch'}}
            boxRef={boxRef}
            boxProps={{
                onMouseDown: (event) => event.stopPropagation(),
                onClick: focus
            }}
        >
            {/* Without children it renders nothing in the box, the list opens at the box (in a portal on the body) */}
            <Popover
                ref={popoverRef}
                anchorRef={boxRef}
                popoverStyle={listMaxWidth ? {maxWidth: listMaxWidth} : undefined}
                onToggle={setOpen}
                disabled={disabled || readOnly}
                noClose
                content={
                    <div tabIndex={-1} style={{maxHeight: 400, display: 'flex', flexDirection: 'column'}}>
                        {values &&
                            <input
                                className={fieldControlClass}
                                style={searchInputStyle}
                                placeholder='Zoeken...'
                                value={inputValue}
                                onChange={onChangeInput}
                                onKeyDown={(event) => {
                                    // The list is at the end of the body, Tab would move on from there: move on from
                                    // the field instead (its focus handler moves focus back here only while open)
                                    if (event.key === 'Tab') {
                                        inputRef.current?.focus()
                                    } else {
                                        onKeyDown(event)
                                    }
                                }}
                                onKeyPress={onKeyPress}
                                onFocus={() => setFocused(true)}
                                onBlur={handleBlur}
                                ref={searchInputRef}
                            />
                        }

                        <div style={{overflowY: 'auto', minHeight: 0}}>
                            {values && selectAll && focused && !inputValue && options.length > 0 &&
                                <ListItem
                                    size='sm'
                                    wrapperStyle={{width: '100%'}}
                                    onMouseDown={onClickSelectAll}
                                >
                                    <Row style={{flexShrink: 0, alignItems: 'center', fontWeight: 'bold'}}>
                                        <span>{allOptionsSelected ? 'Deselecteer alle' : 'Selecteer alle'}</span>
                                    </Row>
                                </ListItem>
                            }

                            {displayOptions.length ?
                                focused && displayOptions.slice(0, maxOptions).map((option, index) => {
                                    if (option) {
                                        const isSelected = values?.includes(option.value?.toString())

                                        return (
                                            <ListItem
                                                size='sm'
                                                style={{
                                                    fontWeight: option.bold ? 'bold' : 'normal',
                                                    // Only when indenting: paddingLeft undefined would remove the item's own padding
                                                    ...(indentSubvalues && option.subvalue ? {paddingLeft: 15} : {})
                                                }}
                                                wrapperStyle={{width: '100%', background: isSelected ? Colors.grey20 : undefined}}
                                                key={`${index}select${id || ''}`}
                                                focused={displayOptions[selectedValue] && displayOptions[selectedValue].value === option.value && displayOptions[selectedValue].subvalue === option.subvalue}
                                                active={(value === option.value && subvalue === (option.subvalue || '')) || isSelected}
                                                onMouseDown={(event) => onClickOption(option, event)}
                                                disabled={option.disabled}
                                            >
                                                <Row style={{flexShrink: listMaxWidth ? 1 : 0, minWidth: 0, alignItems: 'center'}}>
                                                    {option.img &&
                                                        <img
                                                            src={option.img}
                                                            alt={option.title}
                                                            style={{width: 50, height: 24, marginRight: 8, objectFit: 'contain'}}
                                                        />
                                                    }

                                                    {option.icon &&
                                                        <Icon
                                                            style={{color: option.iconColor, marginRight: 6}}
                                                            icon={option.icon}
                                                        />
                                                    }

                                                    {option.badge &&
                                                        <Badge
                                                            size='sm'
                                                            style={{marginRight: 6}}
                                                            color={option.badgeColor}
                                                            textColor={option.badgeTextColor}
                                                        >
                                                            {option.badge}
                                                        </Badge>
                                                    }

                                                    {typeof option.title === 'string' ?
                                                        <span
                                                            title={listMaxWidth ? option.title : undefined}
                                                            style={listMaxWidth ? {minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'} : undefined}
                                                        >
                                                            {boldMatch(option.title, inputValue)}
                                                        </span> :
                                                        option.title
                                                    }

                                                    {isSelected &&
                                                        <Icon
                                                            style={{marginRight: 6, color: Colors.buttonSolid, marginLeft: 'auto', paddingLeft: 6}}
                                                            icon='mdi mdi-check-bold'
                                                        />
                                                    }

                                                </Row>

                                            </ListItem>
                                        )
                                    }
                                }) :
                                !allowCustomValues ?
                                    <ListItem size='sm'><P ellipsis>Geen opties</P></ListItem> :
                                    null
                            }
                            {(allowCustomValues || (allowFreeMode && freeModeAddValue)) && inputValue &&
                                <ListItem
                                    size='sm'
                                    icon='mdi mdi-plus-circle'
                                    onMouseDown={(event) => onClickAddValue(event)}
                                >
                                    <P ellipsis>
                                        {`Voeg "${inputValue}" toe`}
                                    </P>
                                </ListItem>
                            }
                        </div>
                    </div>
                }
            />

            <div style={contentStyle}>
                {values && Array.isArray(values) &&
                    <div
                        ref={tagsRowRef}
                        style={{display: 'flex', flexWrap: 'nowrap', overflow: 'hidden', flex: 1, minWidth: 0, alignItems: 'center', marginLeft: field.paddingX}}
                    >
                        <div style={{position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', display: 'flex', flexWrap: 'nowrap', top: -9999, left: -9999}}>
                            {values.map((value, index) => renderValuePill(value, index, (ref) => valueRefs.current[index] = ref))}

                            {values.length > 0 &&
                                <div ref={overflowTagRef} style={overflowTagStyle}>
                                    {`+${values.length}`}
                                </div>
                            }
                        </div>

                        {values.slice(0, visibleValuesCount).map((value, index) => renderValuePill(value, index))}

                        {visibleValuesCount < values.length &&
                            <div style={overflowTagStyle}>
                                {`+${values.length - visibleValuesCount}`}
                            </div>
                        }
                    </div>
                }

                <input
                    className={fieldControlClass}
                    id={fieldId}
                    style={defaultInputStyle}
                    // readOnly stays focusable (like a readOnly text field); handleFocus and the key handlers ignore it
                    disabled={disabled || loading || optionsLoading}
                    readOnly={readOnly || !!values}
                    value={values ? '' : inputValue}
                    onClick={() => {
                        if (!disabled && !readOnly && focused) {
                            popoverRef.current.show()

                            if (values) {
                                searchInputRef.current?.focus()
                            }
                        }
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={onKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChangeInput}
                    ref={inputRef}
                    role='combobox'
                    aria-expanded={open}
                    aria-invalid={!!invalid}
                />

                {(loading || optionsLoading) &&
                    <Spinner style={{position: 'absolute', left: '50%', top: 15}} />
                }

                {!values && !inputValue &&
                    // Same text position and typography as Input's text: from the box padding, vertically centred. A lone
                    // icon has no label above it, so it's centred in the whole box like the caret
                    <div
                        style={{
                            position: 'absolute',
                            top: showOnlyIcon && option?.icon ? 0 : field.paddingTop,
                            bottom: showOnlyIcon && option?.icon ? 0 : field.paddingBottom,
                            left: option?.img ? 6 : field.paddingX,
                            right: valueRight,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: field.fontSize,
                            color: showsPlaceholder ? formColors.placeholder : getFieldTextColor({disabled, readOnly})
                        }}
                    >
                        {option?.img &&
                            <img
                                src={option.img}
                                alt={option.title}
                                style={{width: 50, height: 24, marginRight: 6, objectFit: 'contain'}}
                            />
                        }

                        {option?.icon &&
                            <Icon
                                style={{color: option.iconColor, marginRight: 6, ...(showOnlyIcon && {fontSize: 24})}}
                                icon={option.icon}
                            />
                        }

                        {option?.badge &&
                            <Badge
                                style={{marginRight: 6}}
                                color={option.badgeColor}
                                textColor={option.badgeTextColor}
                            >
                                {option.badge}
                            </Badge>
                        }

                        {(!showOnlyIcon || !option?.icon) &&
                            <div
                                style={{overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flex: 1, minWidth: 0}}
                                onClick={focus}
                            >
                                {props.showValue ? (option ? option.value : value) : title}
                            </div>
                        }
                    </div>
                }

                {option?.info &&
                    <div style={{position: 'absolute', right: 30, top: 10}}>
                        <Info style={{marginLeft: 6}} color={Colors.buttonSolid} text={option.info} textStyle={option?.infoKeepWhiteSpace ? {whiteSpace: 'pre-wrap'} : undefined} />
                    </div>
                }

                {copy &&
                    <i
                        style={{position: 'absolute', right: option?.info ? 55 : 25, top: 10, cursor: 'pointer', color: getFieldTextColor({disabled, readOnly})}}
                        onClick={onClickCopyValues}
                        className='mdi mdi-content-copy'
                    />
                }

                {!readOnly && <FieldCaret disabled={disabled} readOnly={readOnly} onClick={focus} />}

            </div>

            {copy &&
                // Centred vertically like before, the tooltip opens just below the middle of the field
                <ToolTip visible={showCopyTooltip} text='Gekopieerd naar clipboard' style={{alignSelf: 'center'}}></ToolTip>
            }
        </FieldBox>
    )
})

Select.displayName = 'Select'

export default Select
